import { Mic, Pause, Play, Square, Trash2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";

import { Button } from "@/components/ui";

const MAX_DURATION = 30;
const MIN_DURATION = 5;

interface VoiceRecorderProps {
  value: string | null; // base64 data URI
  onChange: (base64: string | null) => void;
}

export function VoiceRecorder({ value, onChange }: VoiceRecorderProps) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const elapsedRef = useRef(0);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((t) => {
          t.stop();
        });
        if (timerRef.current) clearInterval(timerRef.current);

        if (elapsedRef.current < MIN_DURATION) {
          setError(`Recording too short (min ${MIN_DURATION}s)`);
          setRecording(false);
          setElapsed(0);
          elapsedRef.current = 0;
          return;
        }

        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = () => {
          onChange(reader.result as string);
        };
        reader.readAsDataURL(blob);
        setRecording(false);
      };

      mediaRecorder.start(250);
      setRecording(true);
      setElapsed(0);

      elapsedRef.current = 0;
      let sec = 0;
      timerRef.current = setInterval(() => {
        sec++;
        elapsedRef.current = sec;
        setElapsed(sec);
        if (sec >= MAX_DURATION) {
          mediaRecorder.stop();
        }
      }, 1000);
    } catch {
      setError("Microphone access denied");
    }
  }, [onChange, elapsed]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
  }, []);

  const togglePlay = useCallback(() => {
    if (!value) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(value);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  }, [value, playing]);

  const clear = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    onChange(null);
    setPlaying(false);
    setElapsed(0);
  }, [onChange]);

  return (
    <div>
      <label className="mb-1 block font-medium text-sm text-zinc-700">
        Voice Sample{" "}
        <span className="font-normal text-zinc-400">
          (optional, {MIN_DURATION}-{MAX_DURATION}s)
        </span>
      </label>

      {value ? (
        <div className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
          <Button
            size="icon"
            variant="default"
            onClick={togglePlay}
            className="rounded-full"
          >
            {playing ? <Pause /> : <Play className="ml-0.5" />}
          </Button>
          <div className="flex-1">
            <div className="font-medium text-sm text-zinc-700">Voice recorded</div>
            <div className="text-xs text-zinc-400">Ready for avatar creation</div>
          </div>
          <Button
            onClick={clear}
            size="icon"
            variant="ghost"
            className="text-zinc-500 md:hover:bg-red-50 md:hover:text-red-500"
          >
            <Trash2 />
          </Button>
        </div>
      ) : recording ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="h-3 w-3 animate-pulse rounded-full bg-red-500" />
          <div className="flex-1">
            <div className="font-medium text-red-700 text-sm">
              Recording... {elapsed}s / {MAX_DURATION}s
            </div>
            <div className="mt-1 h-1.5 w-full rounded-full bg-red-200">
              <div
                className="h-1.5 rounded-full bg-red-500 transition-all"
                style={{ width: `${(elapsed / MAX_DURATION) * 100}%` }}
              />
            </div>
          </div>
          <Button
            size="icon"
            onClick={stopRecording}
            className="rounded-full bg-red-600 shadow-red-600/25 md:hover:bg-red-700"
          >
            <Square />
          </Button>
        </div>
      ) : (
        <button
          onClick={startRecording}
          className="flex w-full items-center gap-3 rounded-xl border border-zinc-300 border-dashed px-4 py-3 text-left transition-all hover:border-primary hover:bg-background"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-zinc-500">
            <Mic className="h-5 w-5" />
          </div>
          <div>
            <div className="font-medium text-sm text-zinc-700">Record voice sample</div>
            <div className="text-xs text-zinc-400">
              Speak clearly for {MIN_DURATION}-{MAX_DURATION} seconds
            </div>
          </div>
        </button>
      )}

      {error && <p className="mt-1 text-red-500 text-xs">{error}</p>}
    </div>
  );
}
